import Filter from '@/src/components/organism/Filter';
import { useEffect, useRef, useState } from 'react';
import { creditFilters } from './filter';
import { useQuery } from '@tanstack/react-query';
import useQueryValues from '@/src/hooks/useQueryValues';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { AgGridReact } from 'ag-grid-react';
import Table from '@/src/components/atom/Table';
import { creditHistoryListColumn } from './column';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import Button from '@/src/components/atom/Button';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import { NAV_CONST } from '@/src/constants/nav';
import CreditTemplateManagementModal from './components/CreditTemplateManagementModal';
import {
  getCreditAPI,
  getCreditByIdAPI,
  getCreditDashboardAPI,
  getCreditExcelAPI,
  getCreditSubDescriptionAPI,
  getCreditTypesAPI,
} from '@/src/apis/rest/creditHistory';
import {
  getCreditTemplateAPI,
  getCreditTemplateNameAndCountAPI,
} from '@/src/apis/rest/creditTemplate';
import CreditDetailModal from '@/src/pages/Credit/components/CreditDetailModal';
import DashboardCard from '@/components/organism/DashboardCard';
import { UNIT_STRINGS } from '@/src/constants/unit';
import { formatter } from '@/src/utils/formatter';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';

type ModalState = 'NONE' | 'DETAIL' | 'CREDIT_TEMPLATE_MANAGEMENT';

export default function CreditListPage() {
  // utils
  const { defaultValues } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  // state
  const gridRef = useRef<AgGridReact>(null);
  const [values, setValues] = useState(defaultValues);
  const [modalState, setModalState] = useState<ModalState>('NONE');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // query
  const { data: dashboardData } = useQuery({
    queryKey: ['credit-dashboard'],
    queryFn: getCreditDashboardAPI,
  });
  const { data: creditTemplateNameOptions } = useQuery({
    queryKey: ['credit-template-name-options'],
    queryFn: getCreditTemplateNameAndCountAPI,
    select: data => [
      { label: '전체', value: '' },
      ...(data?.rows?.map(item => ({
        label: item.category,
        value: item.category,
      })) || []),
    ],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const { data: creditSubDescriptionOptions } = useQuery({
    queryKey: ['credit-subdescriptions'],
    queryFn: getCreditSubDescriptionAPI,
    select: data => {
      return [
        { label: '전체', value: '' },
        ...(data?.rows || []).map(item => ({
          label: item.subDescription,
          value: item.subDescription,
        })),
      ];
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: creditTypeOptions } = useQuery({
    queryKey: ['credit-type-options'],
    queryFn: getCreditTypesAPI,
    select: data => [
      { label: '전체', value: '' },
      ...Object.values(data || {}).map(item => ({
        label: item,
        value: item,
      })),
    ],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  console.log('creditTemplateOptions >>>>', creditSubDescriptionOptions);
  const {
    data: creditData,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['credit', values, page, pageSize],
    queryFn: () => {
      return getCreditAPI({
        ...values,
        page,
        pageSize,
      });
    },
  });
  const { data: creditDetailData } = useQuery({
    queryKey: ['credit-detail', selectedId],
    queryFn: () => {
      return selectedId
        ? getCreditByIdAPI({
            id: selectedId,
          })
        : undefined;
    },
    enabled: !!selectedId,
  });

  // event
  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  const handleDetailClick = (id: number) => {
    setSelectedId(id);
    handleOpenModal('DETAIL');
  };

  // effect
  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={
          <div className="flex items-center gap-2">
            <h2>
              <Typo typoType="h26sb">{NAV_CONST.creditManagement}</Typo>
            </h2>
            <Button
              buttonSize="small"
              buttonColor="secondary"
              onClick={() => {
                handleOpenModal('CREDIT_TEMPLATE_MANAGEMENT');
              }}
            >
              크레딧 카테고리 관리
            </Button>
          </div>
        }
        breadcrumbList={[NAV_CONST.creditManagement]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <DashboardCard
          data={[
            {
              key: '총 지급 크레딧',
              value: `${formatter.NUM_COMMA(dashboardData?.provideTotal || 0)}${
                UNIT_STRINGS.CREDIT
              }`,
            },
            {
              key: '총 사용 크레딧',
              value: `${formatter.NUM_COMMA(dashboardData?.useTotal || 0)}${
                UNIT_STRINGS.CREDIT
              }`,
            },
          ]}
        />
        <Filter
          title="검색 필터"
          filters={creditFilters(
            creditTemplateNameOptions || [],
            creditTypeOptions || [],
            creditSubDescriptionOptions || [],
          )}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">얼스 크레딧 목록</Typo>
              <ExcelDownloadButton
                disabled={isFetching || isError || !creditData?.rows?.length}
                fileName="크레딧_내역_리스트"
                downloadExcelAPI={getCreditExcelAPI}
                searchParams={values}
              />
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : creditData?.rows || []}
              isPaginationDataMapping
              columnDefs={creditHistoryListColumn(handleDetailClick)}
              total={creditData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>

      {/* 모달 */}
      {modalState === 'CREDIT_TEMPLATE_MANAGEMENT' && (
        <CreditTemplateManagementModal
          open={modalState === 'CREDIT_TEMPLATE_MANAGEMENT'}
          onOpenChange={() => handleToggleModal('CREDIT_TEMPLATE_MANAGEMENT')}
        />
      )}
      {creditDetailData && modalState === 'DETAIL' && (
        <CreditDetailModal
          open={modalState === 'DETAIL'}
          onOpenChange={e => {
            if (!e) setSelectedId(null);
            handleToggleModal('DETAIL');
          }}
          data={creditDetailData}
        />
      )}
    </>
  );
}
